import { AlertTriangle, FileText, Info, Lightbulb } from 'lucide-react'
import { cn } from '@/utils/cn'

type CalloutVariant = 'info' | 'tip' | 'warning' | 'note'

interface DocsCalloutProps {
  variant?: CalloutVariant
  title?: string
  children: React.ReactNode
}

const variantConfig: Record<
  CalloutVariant,
  { icon: typeof Info; borderColor: string; bgColor: string; iconColor: string; titleColor: string }
> = {
  info: {
    icon: Info,
    borderColor: 'border-info/50',
    bgColor: 'bg-info/10',
    iconColor: 'text-info',
    titleColor: 'text-info',
  },
  tip: {
    icon: Lightbulb,
    borderColor: 'border-success/50',
    bgColor: 'bg-success/10',
    iconColor: 'text-success',
    titleColor: 'text-success',
  },
  warning: {
    icon: AlertTriangle,
    borderColor: 'border-warning/50',
    bgColor: 'bg-warning/10',
    iconColor: 'text-warning',
    titleColor: 'text-warning',
  },
  note: {
    icon: FileText,
    borderColor: 'border-neutral-500/50',
    bgColor: 'bg-neutral-500/10',
    iconColor: 'text-muted-foreground',
    titleColor: 'text-foreground',
  },
}

const defaultTitles: Record<CalloutVariant, string> = {
  info: 'Note',
  tip: 'Tip',
  warning: 'Warning',
  note: 'Note',
}

export function DocsCallout({ variant = 'info', title, children }: DocsCalloutProps) {
  const config = variantConfig[variant]
  const Icon = config.icon
  const displayTitle = title || defaultTitles[variant]

  return (
    <div className={cn('my-6 rounded-lg border-l-4 p-4', config.borderColor, config.bgColor)}>
      <div className="flex items-start gap-3">
        <Icon className={cn('h-5 w-5 mt-0.5 flex-shrink-0', config.iconColor)} />
        <div className="flex-1 min-w-0">
          <div className={cn('font-semibold text-sm mb-1', config.titleColor)}>{displayTitle}</div>
          <div className="text-sm text-foreground leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  )
}
